<script setup>
import {computed, onMounted, onUnmounted, ref} from "vue";
import {useStore} from "vuex";
import {useRoute} from "vue-router";
import ActivitiesFormSheet from "@/components/gamification/seminar/ActivitiesFormSheet.vue";
import TestActivityForm from "@/components/gamification/seminar/TestActivityForm.vue";
import AttendanceEvaluationWindowItem from "@/components/gamification/seminar/AttendanceEvaluationWindowItem.vue";
import QrCode from "@/components/QrCode.vue";
import ActivityInfoRow from "@/components/gamification/seminar/ActivityInfoRow.vue";
import TestWindowItem from "@/components/gamification/seminar/TestWindowItem.vue";
import { useI18n } from 'vue-i18n'
import {TEST_WINDOW_LIMITS, windowInRange, joinWindowFitsDuration} from "@/utils";
const { t } = useI18n()

const store = useStore()
const route = useRoute()

const loading = ref(false)
const errorMessage = ref(null)
const tab = ref(null)
const qrCreated = ref(false)
const tabsItems = ref([])
const regenerationDialog = ref(false)
const regenerationImpact = ref(null)

const activity = computed(()=> store.getters['Activities/activity'])
const separateActivity = computed(()=> store.getters['Activities/separateActivity'])
const testActivityForm = computed(() => store.getters['Activities/testActivityForm']);
const activityTypes = computed({
  get: () => store.getters['Activities/activityTypes'],
  set: (newValue) => {
    store.commit('Activities/setActivityTypes', newValue);
  }
});
const goToTestFromSeminar = computed(()=> store.getters['Seminars/goToTestFromSeminar'])
const testWindowsValid = computed(()=>
  windowInRange(testActivityForm.value.joinWindowMinutes, TEST_WINDOW_LIMITS.join) &&
  joinWindowFitsDuration(testActivityForm.value.joinWindowMinutes, testActivityForm.value.duration)
)
const saveButtonDisabled = computed(()=> {
  let testInfo = true
  if(activityTypes.value.includes('TEST')){
    testInfo = Number(testActivityForm.value.duration) >= 1 && testActivityForm.value.test && testWindowsValid.value
  }
  return activityTypes.value.length === 0 || !testInfo
})
const activityInfo = computed(()=> store.getters['Activities/activityStatusInfo'].map(info => {
  return {
    ...info,
    text: t(info.locKey)
  }
}))
const separateActivityInfo = computed(()=> store.getters['Activities/separateActivityStatusInfo'].map(info => {
  return {
    ...info,
    text: t(info.locKey)
  }
}))

function buildTabs(){
  tabsItems.value = []
  if(activityTypes.value.includes('ATT') || activityTypes.value.includes('EVAL')){
    tabsItems.value.push({key: 'ATT_EVAL', value: 'ATT_EVAL', text: t('gamification.attendance_and_evaluation')})
    tab.value = 'ATT_EVAL'
  }
  if(activityTypes.value.includes('TEST')){
    tabsItems.value.unshift({key: 'TEST', value: 'TEST', text: t('gamification.tests')})
    tab.value = 'TEST'
  }
}

/**
 * Generating QR codes replaces the lesson's activities. Sessions that already collected
 * participants or submissions are archived rather than lost, but the teacher should still know
 * before a misclick retires a test that already ran.
 */
async function requestActivityCreation(){
  loading.value = true
  const impact = await store.dispatch('Activities/fetchRegenerationImpact', {
    lessonId: Number(route.params.seminarId)
  })
  loading.value = false

  if(impact && impact.archivedActivities > 0){
    regenerationImpact.value = impact
    regenerationDialog.value = true
    return
  }

  await createActivity()
}

async function confirmRegeneration(){
  regenerationDialog.value = false
  await createActivity()
}

async function createActivity(){
  loading.value = true
  let response = null
  if(activityTypes.value.includes('TEST')){
    const activityRequest ={
      types: activityTypes.value,
      lessonId: Number(route.params.seminarId),
      testId: testActivityForm.value.test.id,
      duration: Number(testActivityForm.value.duration),
      separateTest: testActivityForm.value.separateTest,
      // null keeps the server defaults - the grace field is hidden in the form, so it is never overridden
      submissionGraceSeconds: null,
      joinWindowMinutes: optionalNumber(testActivityForm.value.joinWindowMinutes)
    }
    response = await store.dispatch('Activities/createActivityWithTest', {
      activityRequest: activityRequest
      })
  } else {
    response = await store.dispatch('Activities/createActivity',{
      activityRequest: { types: activityTypes.value, lessonId: Number(route.params.seminarId)}
    })
  }
  if(response){
    await store.dispatch('Activities/setActivityStatusInfo')
    buildTabs()
    if(activityTypes.value.includes('TEST')){
      await getAccessToken()
      startSubscribe()
    }
    qrCreated.value = true
  }
  loading.value = false
}

async function getAccessToken(){
  await store.dispatch('WebSocketSession/getAccessToken', activity.value.sessionCode)
}


function startSubscribe(){
  store.dispatch('WebSocketSession/disconnect')
  store.dispatch('WebSocketSession/setHeartBeatInterval', null)
  store.dispatch('WebSocketSession/setSocketConfig')
  store.dispatch('WebSocketSession/connect')
}

async function restoreState(activities){
  await store.dispatch('Activities/restoreActivities', {activities})
  activityTypes.value = [...new Set(activities.flatMap(a => a.types))]
  await store.dispatch('Activities/setActivityStatusInfo')
  buildTabs()
  if(activityTypes.value.includes('TEST')){
    restoreTestForm(activities.length > 1)
    await restoreTestSession()
  }
}

function optionalNumber(value){
  return value === null || value === undefined || value === '' ? null : Number(value)
}

function restoreTestForm(separateTest){
  store.commit('Activities/setTestFormField', {key: 'separateTest', value: separateTest})
  store.commit('Activities/setTestFormField', {key: 'duration', value: String(activity.value.timeForTest / 60000)})
  store.commit('Activities/setTestFormField', {key: 'test', value: {id: activity.value.testId, name: activity.value.testName}})
  store.commit('Activities/setTestFormField', {key: 'submissionGraceSeconds', value: null})
  store.commit('Activities/setTestFormField', {key: 'joinWindowMinutes', value: activity.value.joinWindowMinutes ?? null})
}

async function restoreTestSession(){
  if(!activity.value.sessionCode){ return }
  const session = await store.dispatch('WebSocketSession/getTestSessionDetails', activity.value.sessionCode)
  if(!session){ return }
  if(session.status !== 'COMPLETED' && session.status !== 'CANCELLED'){
    await getAccessToken()
    startSubscribe()
  }
  if(session.startedAt){
    store.dispatch('Activities/testExecuted')
    store.dispatch('WebSocketSession/setInitialTimeRemaining', {
      duration: Math.max(session.startedAt + session.duration - Date.now(), 0),
      sessionCode: session.sessionCode
    })
  }
}

onMounted(async ()=>{
  const persistedActivities = store.getters['Seminars/seminar'].activities
  if(!goToTestFromSeminar.value && persistedActivities?.length > 0){
    await restoreState(persistedActivities)
  }
})

onUnmounted(()=>{
  store.dispatch('WebSocketSession/disconnect')
  store.dispatch('Activities/clear')
  if(!goToTestFromSeminar.value){
    activityTypes.value = []
    store.dispatch('Activities/clearTestActivityForm')
  }
})
</script>

<template>
<activities-form-sheet>
  <div class="d-flex w-100 flex-row align-center">
    <div class="pt-5 d-flex flex-column justify-space-between align-start w-50">
      <v-switch v-model="activityTypes" value="ATT" :label="t('gamification.attendance')" color="primary" hide-details />
      <v-switch v-model="activityTypes" value="EVAL" :label="t('gamification.evaluation')" color="primary" hide-details />
      <v-switch v-model="activityTypes" value="TEST" :label="t('gamification.test')" color="primary" hide-details />
      <v-expand-transition>
        <div v-if="activityTypes.includes('TEST')">
          <TestActivityForm class="pt-3" />
        </div>
      </v-expand-transition>
      <div class="text-error">{{errorMessage}}</div>
      <div class="pt-5">
        <v-btn @click="requestActivityCreation" :loading="loading" :disabled="saveButtonDisabled || qrCreated" prepend-icon="qr_code" color="primary" rounded variant="flat">{{$t('gamification.generate_qr_code') }}</v-btn>
      </div>

      <v-dialog v-model="regenerationDialog" max-width="520">
        <v-card>
          <v-card-title>{{ $t('gamification.regenerate_qr_title') }}</v-card-title>
          <v-card-text>
            <p>{{ $t('gamification.regenerate_qr_warning') }}</p>
            <ul class="pl-6 pt-2">
              <li v-if="regenerationImpact?.affectedParticipants">
                {{ $t('gamification.regenerate_qr_participants', {count: regenerationImpact.affectedParticipants}) }}
              </li>
              <li v-if="regenerationImpact?.affectedSubmissions">
                {{ $t('gamification.regenerate_qr_submissions', {count: regenerationImpact.affectedSubmissions}) }}
              </li>
            </ul>
            <p class="pt-3 text-medium-emphasis">{{ $t('gamification.regenerate_qr_kept') }}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="regenerationDialog = false">{{ $t('gamification.cancel') }}</v-btn>
            <v-btn color="primary" variant="flat" @click="confirmRegeneration">{{ $t('gamification.regenerate_qr_confirm') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <div class="d-flex flex-row align-start ga-16 w-50">
      <div v-if="separateActivity.pin">
        <div class="pb-3 ga-3 d-flex flex-column align-start">
          <div class="mb-5">
            <qr-code :qr-data="separateActivity.pin" />
          </div>
          <activity-info-row
              style="max-width: 15em"
              v-for="info in separateActivityInfo"
              :key="info.text"
              :active-color="info.activeColor"
              :text="info.text" />
        </div>
      </div>
      <div v-if="activity.pin">
        <div class="pb-3 ga-3 d-flex flex-column align-start">
          <div class="mb-5">
            <qr-code :qr-data="activity.pin" />
          </div>
          <activity-info-row
              style="max-width: 15em"
              v-for="info in activityInfo"
              :key="info.text"
              :active-color="info.activeColor"
              :text="info.text" />
        </div>
      </div>
    </div>
  </div>
</activities-form-sheet>

  <div v-if="activity.types.length > 0" class="pt-5">
    <v-tabs v-model="tab" color="primary">
      <v-tab
          v-for="item in tabsItems"
          :key="item.key"
          :value="item.value"
          :text="item.text"
      >
      </v-tab>
    </v-tabs>
    <v-tabs-window v-model="tab">
      <v-tabs-window-item
          v-for="item in tabsItems"
          :key="item.key"
          :value="item.value"
      >
        <div v-if="item.value === 'ATT_EVAL'">
          <attendance-evaluation-window-item />
        </div>
        <div v-else>
          <test-window-item />
        </div>
      </v-tabs-window-item>
    </v-tabs-window>
  </div>

</template>

<style scoped>

</style>